class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
  }

  get els() {
    return this.querySelectorAll('[data-announcement]');
  }

  get countdownEls() {
    return this.querySelectorAll('[data-countdown]');
  }

  get linkEls() {
    return this.querySelectorAll('[data-announcement] a');
  }

  get navNext() {
    return this.querySelector('[data-nav="next"]');
  }

  get navPrev() {
    return this.querySelector('[data-nav="prev"]');
  }

  get freeShippingMessages() {
    return this.querySelectorAll('[data-free-shipping-message]');
  }

  get freeShippingThresholds() {
    return this.querySelectorAll('[data-announcement-threshold]');
  }

  get autoplayControl() {
    return this.querySelector('.announcement__autoplay-control');
  }

  connectedCallback() {
    // Liquid fallback for no active announcements
    if (!this.els.length) {
      this.parentElement.classList.add('hide');
      return;
    }

    // Primary settings
    this.hasFreeShippingBanner = this.dataset.hasFreeShippingBanner;
    this.indexActive = 0;
    this.indexLast = this.els.length - 1;

    // Secondary settings
    this.hasAutoplay = this.dataset.autoplay && this.dataset.autoplay > 0;
    this.hasCarousel = this.els.length > 1;

    // Setup and listeners
    if (this.els.length) this.setItemAttrs();
    if (this.countdownEls.length) this.handleCountdowns();
    if (this.hasCarousel) {
      if (this.hasAutoplay) this.autoplayTimerStart();
      this.handleNav();
      this.handleDrag();
    }
    if (this.hasFreeShippingBanner) this.freeShippingBanner();
    if (this.autoplayControl) this.handleAutoplayControl();
  }

  // Set index-based attributes of announcement items
  setItemAttrs() {
    this.els.forEach((el, i) => {
      const animationDuration = i === 0 ? 'var(--announcement-animation-duration, 300ms)' : '300ms';
      const opacity = i === 0 ? 'var(--announcement-opacity, 0)' : 0;
      const transformY = i === 0 ? 'var(--announcement-transform-y, 2px)' : '2px';

      el.dataset.index = i;
      if (i === 0) {
        el.classList.remove('hide');
        el.classList.add('active');
        el.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', 0));
      }
      el.style.animation = `fadeIn ${animationDuration} forwards 1 ease-in-out`;
      el.style.opacity = opacity;
      el.style.transform = `var(--prefers-reduced-motion-none, translate3d(0, ${transformY}, 0))`;
    });
  }

  autoplayTimerStart() {
    this.timerIsPaused = false;
    this.timerIsStopped = false;

    this.timer = setInterval(() => {
      this.els[this.indexActive]?.classList.replace('active', 'hide');
      this.indexActive = this.indexActive + 1 > this.indexLast ? 0 : this.indexActive + 1;

      requestAnimationFrame(() => {
        this.els[this.indexActive]?.classList.replace('hide', 'active');
        this.els.forEach((el, i) => {
          el.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', i === this.indexActive ? 0 : -1));
        })
      });
    }, this.dataset.autoplay);
  }

  autoplayTimerPause() {
    if (!this.hasAutoplay || this.timerIsPaused || this.timerIsStopped) return;

    this.timerIsPaused = true;
    clearInterval(this.timer);
  }

  autoplayTimerStop() {
    if (!this.hasAutoplay || this.timerIsStopped) return;

    this.timerIsStopped = true;
    clearInterval(this.timer);
  }

  freeShippingBanner() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event.source === 'product-form') return;
      this.updateFreeShippingBanner(event.cartData.total_price);
    });
  }

  handleAutoplayControl() {
    this.autoplayControl.addEventListener('click', () => {
      const spans = this.autoplayControl.querySelectorAll('span');
      spans.forEach(el => el.classList.toggle('hide'));
      !this.timerIsPaused && !this.timerIsStopped ? this.autoplayTimerPause() : this.autoplayTimerStart();
    })
    
  }

  handleNav() {
    [this.navNext, this.navPrev].forEach(el => {
      if (this.dataset.hideArrows === 'true') return;

      el.classList.remove('hide');

      el.addEventListener('click', () => {
        requestAnimationFrame(() => {
          this.autoplayTimerStop();
          this.navUpdate(el.dataset.nav);
          this.autoplayControl.querySelectorAll('span').forEach((el, i) => {
            i == 0 ? el.classList.remove('hide') : el.classList.add('hide');
          });
        });

        this.classList.add('interacted');
      });
    })
  }

  handleDrag() {
    const threshold = 50;
    let isDragging = false;
    let startX = 0;
    let currentX = 0;

    this.style.touchAction = 'pan-y';

    const onPointerMove = e => {
      if (!isDragging) return;
      currentX = e.clientX;
    };

    const onPointerUp = () => {
      if (!isDragging) return;
      isDragging = false;
      this.classList.remove('dragging');

      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);

      const deltaX = currentX - startX;

      if (Math.abs(deltaX) > threshold) {
        this.autoplayTimerStop();
        this.navUpdate(deltaX < 0 ? 'next' : 'prev');
        this.classList.add('interacted');

        this.autoplayControl?.querySelectorAll('span').forEach((el, i) => {
          i == 0 ? el.classList.remove('hide') : el.classList.add('hide');
        });
      } else if (this.hasAutoplay && !this.timerIsStopped) {
        this.autoplayTimerStart();
      }

      startX = 0;
      currentX = 0;
    };

    const onPointerDown = e => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('.announcement__autoplay-control, [data-nav]')) return;

      isDragging = true;
      startX = e.clientX;
      currentX = startX;
      this.classList.add('dragging');
      this.autoplayTimerPause();

      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      window.addEventListener('pointercancel', onPointerUp);
    };

    this.addEventListener('dragstart', e => e.preventDefault());
    this.addEventListener('pointerdown', onPointerDown);
  }

  navUpdate(direction) {
    const nextIndex = this.indexActive + 1 > this.indexLast ? 0 : this.indexActive + 1;
    const prevIndex = this.indexActive - 1 < 0 ? this.indexLast : this.indexActive - 1;
    const newIndex = direction === 'prev' ? prevIndex : nextIndex;
    this.els[this.indexActive].classList.replace('active', 'hide');
    this.els[newIndex].classList.replace('hide', 'active');
    this.indexActive = newIndex;
  }

  now(timezone) {
    return Date.parse(typeof timezone !== 'undefined' ? new Date().toLocaleString('en-US', {timeZone: timezone}) : new Date())
  }

  updateFreeShippingBanner(total) {
    const away = Math.abs(total - window.freeShipping?.amount);
    const hasFreeShipping = total >= window.freeShipping?.amount;

    this.freeShippingMessages.forEach(el => {
      const type = el.dataset.freeShippingMessage;

      if (hasFreeShipping) {
        el.classList[type === 'met' ? 'remove' : 'add']('hide');
      } else {
        el.classList[type === 'met' ? 'add' : 'remove']('hide');
        this.freeShippingThresholds.forEach(el => el.innerHTML = window.formatMoney(away).replace('.00', ''));
      }
    });
  }

  /***
  * Countdown Timers
  */
  handleCountdowns() {
    this.countdownTimers = [];
    this.countdownEls.forEach((el, index) => {
      let data = null;

      try {
        // Run times are set in the Announcement Item block
        data = JSON.parse(el.parentElement.dataset.runTimes);
      } catch (error) {
        console.error('Unable to parse countdown settings:', error);
      }

      if (data) {
        const ends = new Date(data.end).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
        const starts = new Date(data.start).toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });

        this.countdownCreate({
          el,
          ends,
          endDate: data.end ? new Date(ends).getTime() : null,
          startDate: data.start ? new Date(starts).getTime() : null,
          timezone: 'America/Los_Angeles',
        }, index);
      }
    });
  }

  /**
  * Create countdown
  */
  countdownCreate(settings, index) {
    const endDate = settings.endDate + this.timezoneOffset(settings);
    const today = this.now(settings.timezone);
    const dateHasPassed = Math.sign(endDate - today) >= 0;

    settings.el.title = `Ends ${settings.ends}`;

    if (settings.endDate && dateHasPassed) {
      this.countdownUpdate(settings, settings.el);
      this.countdownTimers[index] = setInterval(() => this.countdownUpdate(settings, settings.el, index), 1000);
    } else {
      settings.el.innerHTML = `
        <span class="countdown__hours">00h:</span>
        <span class="countdown__minutes">00m:</span>
        <span class="countdown__seconds">00s</span>
      `;
    }
  }

  /**
  * Update Timer
  */
  countdownUpdate(settings, $timer, index) {
    const now = this.now(settings.timezone);
    const endDate = settings.endDate + this.timezoneOffset(settings);
    const distance = endDate - now;

    const days = Math.floor(distance / (1000 * 60 * 60 * 24))
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });
    const seconds = Math.floor((distance % (1000 * 60)) / 1000)
      .toLocaleString(undefined, { minimumIntegerDigits: 2 });

    if (days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) {
      $timer.innerHTML = `
        <span class="countdown__hours">00h:</span>
        <span class="countdown__minutes">00m:</span>
        <span class="countdown__seconds">00s</span>
      `;
      clearInterval(this.timers[index]);
    } else {
      $timer.innerHTML = `
        ${days < 1 ? '' : `<span class="countdown__days">${days}d:</span>`}
        <span class="countdown__hours">${hours}h:</span>
        <span class="countdown__minutes">${minutes}m</span>
        ${days > 0 ? '' : `:<span class="countdown__seconds">${seconds}s</span>`}
      `;
    }
  }

  timezoneOffset(settings) {
    return Math.abs(this.now(settings.timezone) - this.now()) // difference in milliseconds
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar);
}
